import { defineStore } from 'pinia';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import { fetchAIFlags } from '@/vuex/modules/ai-flags/ai-flags.utils';

interface AIFlag {
    flag: string;
    canEnable: boolean;
    [key: string]: unknown;
}

interface AIFlagsState {
    subscriptionId: string | undefined;
    map: Record<string, AIFlag>;
    error: unknown;
    fetching: boolean;
}

let loadAllPromise: Promise<void> | null = null;

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}

export function getInitialState (): AIFlagsState {
    return {
        subscriptionId: undefined,
        map: {},
        error: {},
        fetching: false,
    };
}

export const useAiFlagsStore = defineStore('aiFlags', {
    state: getInitialState,
    getters: {
        aiFlagsMap: (state) => state.map,
        canEnableAIFlag: (state) => (name: string): boolean => {
            const flag = state.map?.[name];

            return flag ? flag.canEnable : false;
        },
    },
    actions: {
        async hydrate ({ aiSettings }: { aiSettings?: AIFlag[] } = {}) {
            if (!aiSettings) {
                await this.loadAll({ reFetch: true });

                return;
            }

            this.map = keyBy(aiSettings, 'flag');
        },
        async loadAll ({ reFetch = false, subscriptionId, isSuperSettings = false }: { reFetch?: boolean; subscriptionId?: string; isSuperSettings?: boolean } = {}) {
            if (!isEmpty(this.map) && !reFetch && this.subscriptionId === subscriptionId) {
                return;
            }
            if (loadAllPromise && !reFetch) {
                return loadAllPromise;
            }

            this.fetching = true;
            loadAllPromise = (async () => {
                try {
                    this.subscriptionId = subscriptionId;
                    const aiSettings = await fetchAIFlags(subscriptionId, isSuperSettings);
                    this.fetching = false;
                    this.map = keyBy(aiSettings, 'flag');
                } catch (error) {
                    this.subscriptionId = undefined;
                    this.fetching = false;
                    this.error = error;
                    // Don't explode
                }
            })().finally(() => {
                loadAllPromise = null;
            });

            return loadAllPromise;
        },
    },
});
